import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/lib/Button';

import { aOrAn, capitalizeWords } from 'helpers/HelperFunctions';

import Label from 'components/Label/Label';
import Markdown from 'components/Shared/Markdown';
import Panel from 'components/Panel/Panel';
import Price from 'components/Price/Price';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import Modal from 'components/Modal/Modal';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import Verified from 'components/Badges/Verified/Verified2';
import ExternalClient from 'components/Badges/ExternalClient/ExternalClient';
import ReportUserModal from 'components/ReportUserModal/ReportUserModal';

import briefShape from 'propTypes/briefShape';
import styles from './BriefDetail.module.scss';
import SignUp from 'components/SignUp/SignUp';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

class BriefDetail extends React.Component {
  static propTypes = {
    brief: briefShape,
    isProUser: PropTypes.bool,
    owner: PropTypes.shape({
      avatars: PropTypes.object,
      contact_options: PropTypes.object,
      displayname: PropTypes.string,
      last_active: PropTypes.string,
      links: PropTypes.shape({
        main: PropTypes.string,
      }),
      location: PropTypes.shape({
        text: PropTypes.string,
      }),
      type: PropTypes.oneOf(['seller', 'buyer']),
      user: PropTypes.object,
      organisation: PropTypes.bool,
      sector: PropTypes.string,
      is_external: PropTypes.bool,
    }),
    isExpired: PropTypes.bool,
    isExternal: PropTypes.bool,
    user: PropTypes.object,
    onBookmarkClick: PropTypes.func,
    isSaved: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      showReportModal: false,
      showSignUpModal: false,
    };
  }

  handleShowReportModal = () => {
    this.setState({ showReportModal: true });
  };

  componentDidMount() {
    if (window.location.href.includes('report=1')) {
      this.setState({ showReportModal: true });
    }
  }

  render() {
    const {
      brief,
      owner,
      isProUser,
      isExpired,
      isExternal,
      isSaved,
      onBookmarkClick,
    } = this.props;

    let ownerInfoAvatar = (
      <Thumbnail
        thumbnails={owner.avatars}
        circle
        className={styles.ownerAvatar}
      />
    );

    let isPrivate = false;
    if (owner.displayname.match(/user[0-9]+/i)) {
      isPrivate = true;
    }
    const ownerDisplayName = (
      <span className={styles.ownerName}>
        <strong>
          {isPrivate ? 'Private' : owner.displayname} <Verified user={owner} />{' '}
        </strong>
      </span>
    );

    const ownerInfo = (
      <div className={styles.clearfix}>
        {
          <div className={styles.nameLocationWrapper}>
            <div className={styles.avatarNameWrapper}>
              <div className={styles.avatar}>{ownerInfoAvatar}</div>
              <div className={styles.displayname}>{ownerDisplayName}</div>
              {owner.is_external && <ExternalClient />}
            </div>
            {owner.organisation &&
              owner.sector &&
              owner.sector.toLowerCase() != 'other' && (
                <div>
                  {`${capitalizeWords(
                    aOrAn(owner.sector)
                  )} ${owner.sector.toLowerCase()} company`}
                </div>
              )}
            {owner.location.text != 'Private' && (
              <div>Based in {owner.location.text}</div>
            )}
            <div className={styles.ownerLastActive}>
              <div>Last online {moment(owner.last_active).fromNow()}</div>
            </div>
          </div>
        }
      </div>
    );

    let city = '';
    let country = '';
    if (!brief.remote) {
      const locationArr = brief.location.split(', ');
      city = locationArr[0];
      country = locationArr[1];
    }

    const hasNoContactOptions = Object.values(owner.contact_options).every(
      (v) => v === false
    );
    const { email, phone, sms } = owner.contact_options;

    return (
      <div itemScope itemType="https://schema.org/JobPosting">
        <div
          className="hide"
          itemProp="hiringOrganization"
          itemScope
          itemType="https://schema.org/Organization"
        >
          <span itemProp="name" content="Twine" />
          <span itemProp="sameAs" content="https://www.twine.net" />
          <span
            itemProp="logo"
            content="https://www.twine.net/assets/img/logos/twine-social-icon-min.png"
          />
        </div>
        {brief.role ? (
          <div itemProp="title" content={`Freelance ${brief.role}`} />
        ) : (
          <div
            itemProp="title"
            content={`Freelancer for ${brief.skill} Project`}
          />
        )}
        <div itemProp="datePosted" content={brief.approved_at} />
        <div itemProp="validThrough" content={brief.expires_at} />
        <div itemProp="employmentType" content="CONTRACTOR" />
        {brief.remote ? (
          <div>
            <div itemProp="jobLocationType" content="TELECOMMUTE" />
            <div
              itemProp="applicantLocationRequirements"
              itemScope
              itemType="https://schema.org/Country"
            >
              <span itemProp="name" content="USA" />
              <span itemProp="name" content="United Kingdom" />
              <span itemProp="name" content="Canada" />
              <span itemProp="name" content="Australia" />
              <span itemProp="name" content="Austria" />
              <span itemProp="name" content="Belarus" />
              <span itemProp="name" content="Belgium" />
              <span itemProp="name" content="Denmark" />
              <span itemProp="name" content="France" />
              <span itemProp="name" content="Germany" />
              <span itemProp="name" content="Greece" />
              <span itemProp="name" content="Italy" />
              <span itemProp="name" content="Netherlands" />
              <span itemProp="name" content="Portugal" />
              <span itemProp="name" content="Russia" />
              <span itemProp="name" content="Spain" />
              <span itemProp="name" content="Hong Kong" />
              <span itemProp="name" content="Malaysia" />
              <span itemProp="name" content="Singapore" />
              <span itemProp="name" content="South Korea" />
              <span itemProp="name" content="United Arab Emirates" />
            </div>
          </div>
        ) : (
          <div
            itemProp="jobLocation"
            itemScope
            itemType="https://schema.org/Place"
          >
            <span
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
            >
              <span itemProp="addressLocality" content={city} />
              <span itemProp="addressCountry" content={country} />
            </span>
          </div>
        )}
        <Panel className={styles.container} noPadding={true}>
          <Panel.Item>
            <div className={styles.briefHeader}>
              <h1 className={styles.briefTitle} title={brief.text}>
                {brief.text}
              </h1>
              <div className={styles.briefHeaderRight}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip
                      id="tooltip"
                      style={{ fontSize: '14px', fontFamily: 'Lato' }}
                    >
                      {isSaved ? 'Unsave Job' : 'Save Job'}
                    </Tooltip>
                  }
                >
                  <div
                    className={styles.bookmarkContainer}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!this.props.user) {
                        this.setState({ showSignUpModal: true });
                      } else {
                        onBookmarkClick();
                      }
                    }}
                  >
                    <SvgIcon
                      className={styles.bookmarkIcon}
                      icon={isSaved ? 'bookmark' : 'bookmark-outline'}
                      color="#777"
                      size="25"
                    />
                  </div>
                </OverlayTrigger>
                {((brief && brief.priority) ||
                  (brief && brief.num_of_pitches >= 1)) && (
                  <div style={{ marginTop: '3px' }} className={styles.priority}>
                    {brief && brief.boosted_expires_at && !isExpired && (
                      <span title="This client urgently wants to hire">
                        <div
                          className={`${styles.popularityLabel} ${styles.urgent}`}
                        >
                          Priority job
                        </div>
                      </span>
                    )}
                    {brief &&
                      !brief.boosted_expires_at &&
                      brief.num_of_pitches >= 10 &&
                      !isExpired && (
                        <span title="Pitch soon – this client has received pitches">
                          <div
                            className={`${styles.popularityLabel} ${styles.popular}`}
                          >
                            {brief.num_of_pitches >= 20 ? 'Very ' : ''}Popular
                            job
                          </div>
                        </span>
                      )}
                    {brief &&
                      !brief.boosted_expires_at &&
                      brief.num_of_pitches < 10 &&
                      !isExpired && (
                        <span title="Pitch before other freelancers">
                          <div
                            className={`${styles.popularityLabel} ${styles.low}`}
                          >
                            Open job
                          </div>
                        </span>
                      )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.briefInfo}>
              <Label className={styles.roleLabel}>
                {brief.role || brief.skill}
              </Label>
              {brief.amount && brief.amount !== 0 && brief.amount !== -1 ? (
                <Label className={styles.budgetLabel}>
                  <span>💰 </span>
                  <Price currency={brief.currency} amount={brief.budget} />
                </Label>
              ) : (
                <Label className={styles.budgetLabel}>
                  <span>💰 Negotiable</span>
                </Label>
              )}
              <Label className={styles.locationLabel}>
                {brief.remote ? (
                  <span title="Remote job">🌎 Remote</span>
                ) : (
                  <span title={`${brief.text} – ${brief.location}`}>
                    📍 {brief.location}
                  </span>
                )}
              </Label>
            </div>
            {brief.active && (
              <div className={styles.timeframe}>
                Closing date: {moment(brief.expires_at).fromNow(true)} left
              </div>
            )}
            {brief && brief.num_of_pitches >= 5 && !isExpired && (
              <div className={styles.popularity}>
                Applications: {brief.num_of_pitches} freelancer
                {brief.num_of_pitches > 1 ? 's' : ''} applied
              </div>
            )}
          </Panel.Item>
          <Panel.Item className={styles.ownerInfo}>
            {owner.type === 'seller' ? (
              <Link to={owner.links.main} className={styles.ownerInfoWrapper}>
                {ownerInfo}
              </Link>
            ) : (
              <div className={styles.ownerInfoWrapper}>{ownerInfo}</div>
            )}
          </Panel.Item>
          {brief.spec !== '' ? (
            <Panel.Item>
              <div itemProp="description" className={styles.briefDescription}>
                <Markdown
                  text={
                    (!brief.remote
                      ? `${brief.role || brief.skill} is needed in ${
                          brief.location
                        }.\n\n`
                      : '') + brief.spec
                  }
                />
                {!isExternal && (
                  <div className={styles.contactOptions}>
                    <p className="mb">
                      <b>Client contact preference:</b>
                    </p>
                    {hasNoContactOptions ? (
                      <span>On Twine (contact details private)</span>
                    ) : (
                      <>
                        {email && (
                          <>
                            <span className={styles.contactOption}>
                              📧 Email
                            </span>
                          </>
                        )}
                        {sms && (
                          <>
                            <span className={styles.contactOption}>
                              📱 WhatsApp / SMS
                            </span>
                          </>
                        )}
                        {phone && (
                          <>
                            <span className={styles.contactOption}>
                              📞 Phone
                            </span>
                          </>
                        )}
                        {isProUser ? (
                          <p className="mt">
                            As a pro user, you will get access to the above
                            contact details when you apply for this job
                          </p>
                        ) : (
                          <p className="mt">
                            <a href="/pricing/creative">Twine Pro</a> members
                            will get direct contact details of this client after
                            applying.{' '}
                            <a href="/pricing/creative">Upgrade now</a>.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div className={styles.briefFooter}>
                  <div className={styles.postedAgo}>
                    Posted {moment(brief.approved_at).fromNow()}
                  </div>
                  {this.props.user && (
                    <div className={styles.report}>
                      <Button
                        className="ml"
                        block
                        bsStyle="info"
                        bsSize="sm"
                        onClick={() => {
                          this.handleShowReportModal();
                        }}
                      >
                        <SvgIcon icon="flag" size="14" /> Report user
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Panel.Item>
          ) : null}
          <Modal
            show={this.state.showReportModal}
            closebutton
            onExit={() => {
              this.setState({ showReportModal: false });
            }}
          >
            <ReportUserModal
              accusedUser={this.props.owner}
              claimant={this.props.user}
              isBrief={true}
              onClose={() => this.setState({ showReportModal: false })}
              brief={this.props.brief}
            />
          </Modal>
          {!this.props.user && (
            <Modal
              show={this.state.showSignUpModal}
              subtitle="Sign-up now to get jobs straight to your inbox"
              closeButton
              onExit={() => {
                this.setState({
                  showSignUpModal: false,
                });
              }}
            >
              <SignUp
                redirect={'onboarding'}
                redirectFromGoogle={'signup'}
                defaultAccountType={'seller'}
              />
            </Modal>
          )}
        </Panel>
      </div>
    );
  }
}

export default BriefDetail;
